import React from "react";
import { Link } from "react-router-dom";
import { FaInstagram } from "react-icons/fa"
export default function PlagiarismFreeArticleFull() {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">
      {/* subtle glow accents */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
         How to Make Your Assignment Plagiarism-Free the Right Way
          </h1>
          <p className="mt-3 text-gray-300">
            MD- Make your assignment plagiarism-free the right way: similarity vs plagiarism, ethical paraphrasing, in-text citations, Turnitin tips.
          </p>

         
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* TOC (sticky on desktop) */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-5">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#intro" className="block hover:text-white">Introduction</a>
                <a href="#keypoints" className="block hover:text-white">5 key points</a>
                <a href="#quickwins" className="block hover:text-white">Quick Wins</a>
                <a href="#similarity" className="block hover:text-white">Similarity vs Plagiarism</a>
                <a href="#fixfirst" className="block hover:text-white">What to fix first</a>
                <a href="#types" className="block hover:text-white">Types of Plagiarism</a>
                <a href="#paraphrase" className="block hover:text-white">Ethical Paraphrasing (Mini Examples)</a>
                <a href="#scores" className="block hover:text-white">Acceptable Similarity Scores</a>
                <a href="#ai" className="block hover:text-white">AI Writing vs Similarity</a>
                <a href="#common" className="block hover:text-white">Common Knowledge & Citation</a>
                <a href="#figures" className="block hover:text-white">Figures, Tables & Data</a>
                <a href="#styles" className="block hover:text-white">Citation Styles</a>
                <a href="#turnitin" className="block hover:text-white">Interpreting Turnitin</a>
                <a href="#workflow" className="block hover:text-white">Repeatable Workflow</a>
                <a href="#consequences" className="block hover:text-white">Consequences & Integrity</a>
                <a href="#support" className="block hover:text-white">What we provide</a>
                <a href="#faq" className="block hover:text-white">FAQs</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
                 <a href="#acknowledgement" className="block hover:text-white">Acknowledgement</a>
              </nav>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-8 space-y-10">
            {/* Introduction */}
            <section id="intro" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Introduction</h2>
              <p className="text-gray-200">
                In order to make an assignment plagiarism-free, separate similarity from plagiarism, then write from genuine understanding. Treat the Similarity Report (e.g., Turnitin) as a diagnostic, not a verdict; paraphrase ethically (avoid patchwriting), add precise in-text citation, and keep a clean, consistent reference list (APA 7th, Harvard referencing, or MLA 9).
              </p>
              <p className="text-gray-200 mt-3">
                Quote only when wording is essential, synthesize multiple sources in your own structure, and if your course permits follow your AI writing policy transparently. Do this, and you’ll lower matching text while protecting academic integrity.
              </p>
            </section>

            {/* 5 key points */}
            <section id="keypoints" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">5 key points :</h2>
              <ul className="list-disc pl-6 mt-2 text-gray-200 space-y-2">
                <li>Separate similarity from plagiarism; use the Similarity Report as guidance, not a grade.</li>
                <li>Paraphrase ethically (Read–Hide–Write–Check); change structure, not just words; add in-text citation.</li>
                <li>Cite as you draft; ensure every citation maps to a consistent reference list (APA/Harvard/MLA).</li>
                <li>Synthesize multiple sources per paragraph; quote sparingly when wording is distinctive or technical.</li>
                <li>Recheck before submission; filter refs/short matches, fix high-overlap passages, and follow your AI writing and style policies.</li>
              </ul>
            </section>

            {/* Quick Wins */}
            <section id="quickwins" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">How to Make My Assignment Plagiarism-Free: Quick Wins</h2>
              <div className="text-gray-200 space-y-3">
                <p>Outline first so your argument and structure are yours, this naturally lowers similarity.</p>
                <p>Paraphrase with a “read–hide–write–check” routine to avoid patchwriting and mosaic plagiarism.</p>
                <p>Add in-text citations while drafting (don’t postpone them); finish with a consistent bibliography.</p>
                <p>Scan with a plagiarism checker to find overlapping phrasing; then fix uncited paraphrases and consolidate quotes.</p>
                <p>Rework any single-source paragraphs into comparative analysis that synthesizes multiple sources.</p>
                <p>Confirm that references and quotations are formatted correctly; re-run your check if you made substantial edits.</p>
              </div>
            </section>

            {/* Similarity vs Plagiarism */}
            <section id="similarity" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Similarity vs Plagiarism: What Your Report Really Means</h2>
              <p className="text-gray-200">
                A Similarity Report shows overlap, not guilt. It will highlight quotations, titles, headings, and even your reference list. Educators and editors review context: Was the idea credited? Are quotations marked and cited? Is the match just a standard phrase or a properly cited definition?
              </p>
              <p className="text-gray-200 mt-3">
                A relatively low percentage can still mask paraphrasing plagiarism; a higher percentage may be acceptable if citations and quotations are correctly used.
              </p>

              <div className="mt-4">
                <h3 className="text-lg font-semibold text-white">What to fix first when you see a high percentage:</h3>
                <ul className="list-disc pl-6 mt-2 text-gray-200 space-y-2">
                  <li>Exclude the bibliography and small matches if your checker allows it.</li>
                  <li>Confirm all direct quotes have quotation marks and page numbers (if required).</li>
                  <li>Find paragraphs with heavy overlap and apply ethical paraphrasing.</li>
                  <li>Replace over-quoting with your own synthesis.</li>
                  <li>Ensure every borrowed idea has in-text citation that maps to your reference list.</li>
                </ul>
              </div>
            </section>

            {/* Types */}
            <section id="types" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Types of Plagiarism (Short, Real-World Examples)</h2>
              <div className="text-gray-200 space-y-3">
                <p><strong>Verbatim plagiarism:</strong> Copying text word-for-word without quotation marks or citation.</p>
                <p><strong>Patchwriting / mosaic plagiarism:</strong> Swapping a few words but keeping the source’s structure and rhythm.</p>
                <p><strong>Paraphrasing plagiarism:</strong> Rephrasing an idea but failing to credit its source.</p>
                <p><strong>Source-based plagiarism:</strong> Misrepresenting sources, citing one while relying on another, or fabricating references.</p>
                <p><strong>Self-plagiarism:</strong> Reusing substantial parts of your past assignments without permission or disclosure.</p>
                <p>Having these in your article clarifies what readers must avoid—and why your method works.</p>
              </div>
            </section>

            {/* Paraphrasing examples */}
            <section id="paraphrase" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">How to Make My Assignment Plagiarism-Free with Ethical Paraphrasing (Mini Examples)</h2>

              <div className="text-gray-200 space-y-3">
                <h3 className="font-semibold">The Read–Hide–Write–Check Method</h3>
                <p><strong>Read:</strong> Understand the idea and its context; note author, year, and page.</p>
                <p><strong>Hide:</strong> Close the source so you don’t mirror its structure.</p>
                <p><strong>Write:</strong> Explain the idea in your voice, integrated into your argument, then add the in-text citation.</p>
                <p><strong>Check:</strong> Compare with the original; confirm you’ve changed structure and wording but preserved meaning.</p>

                <div>
                  <h4 className="font-semibold mt-3">Example 1</h4>
                  <p className="mt-2">Original: “Academic integrity policies require students to acknowledge the work of others to maintain trust and credibility in scholarly communication.”</p>
                  <p className="mt-2">Patchwritten (wrong): “Academic integrity rules require learners to recognize the work of others to uphold trust and credibility in academic communication.”</p>
                  <p className="mt-2">Ethical paraphrase: Universities expect students to credit any borrowed ideas because attribution sustains trust and the reliability of scholarly work (academic integrity; Author, Year).</p>
                </div>

                <div>
                  <h4 className="font-semibold mt-3">Example 2</h4>
                  <p className="mt-2">Original: “A literature review synthesizes existing studies to reveal patterns, gaps, and debates in a topic area.”</p>
                  <p className="mt-2">Patchwritten (wrong): “A literature review combines current studies to show trends, gaps, and debates in a field.”</p>
                  <p className="mt-2">Ethical paraphrase: The purpose of a literature review is to compare and connect prior findings so you can see what’s consistent, what’s disputed, and where more evidence is needed (Author, Year).</p>
                </div>

                <p className="mt-3">These examples make the difference between patchwriting and ethical paraphrasing unmistakable</p>
              </div>
            </section>

            {/* Similarity scores */}
            <section id="scores" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">“Acceptable” Similarity Scores: The Nuance Students Miss</h2>
              <p className="text-gray-200">
                There is no universal “good percentage.” Some instructors tolerate a modest percentage if it’s mostly quotations and references; others expect very low numbers depending on the task and discipline. Aim for a report where highlighted text is either (a) quotation you intentionally used and cited, or (b) paraphrase with proper attribution. If a match shows up for your unique phrasing, re-word the sentence; if a match appears because you described a method common to your field, add a citation and move on.
              </p>
            </section>

            {/* AI section */}
            <section id="ai" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">AI Writing vs Similarity (2025 Reality)</h2>
              <p className="text-gray-200">
                AI writing detection and Similarity Reports are different. A similarity check compares your text to published materials and repositories; AI writing indicators assess patterns consistent with machine-generated language. Institutions vary on whether and how you may use AI tools.
              </p>
              <p className="text-gray-200 mt-3">
                If your course allows AI for brainstorming or proofreading, follow disclosure rules, verify facts, and avoid fabricated references. If your policy restricts AI-generated content, keep your draft human-written and document your research trail.
              </p>
              <p className="mt-3">
  <Link 
    to="/writewithai" 
    className="text-blue-400 font-semibold hover:text-blue-300 underline"
  >
    Learn how to write with AI and still get a good grade
  </Link>
</p>

            </section>

            {/* Common Knowledge */}
            <section id="common" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Common Knowledge, Secondary Citation, and When to Cite</h2>
              <p className="text-gray-200">
                Common knowledge: Widely known facts (“water boils at 100°C at sea level”) don’t require citations. Field-specific facts may still need citation, err on the side of crediting unless your discipline treats it as foundational knowledge taught everywhere.
              </p>
              <p className="text-gray-200 mt-3">
                Secondary citation: If you learned an idea from Source B but the idea originates in Source A and you can’t access A, note both in your prose and cite B according to your style guide. Where possible, locate the original and cite it directly.
              </p>
              <p className="text-gray-200 mt-3">
                This short section prevents two extremely common mistakes: over-citing obvious facts and under-citing influential ideas.
              </p>
            </section>

            {/* Figures */}
            <section id="figures" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Figures, Tables, and Data: How to Credit Without Overwhelm</h2>
              <p className="text-gray-200">
                If you reuse or adapt a figure or table, include a caption such as “Adapted from …” and give full credit in your reference list. For images and datasets, check the license (e.g., Creative Commons) and comply with attribution requirements. When in doubt, redraw from scratch and cite the data source. Provide DOIs or stable URLs where your style permits.
              </p>
            </section>

            {/* Citation styles */}
            <section id="styles" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Citation Styles Without the Headache</h2>
              <p className="text-gray-200">Pick one and stick to it:</p>

              <div className="text-gray-200 mt-3 space-y-3">
                <p><strong>APA 7th:</strong> Author–date emphasis; commonly used in social sciences.</p>

                <p><strong>Harvard referencing:</strong> Similar author–date approach with institutional variations.</p>

                <p><strong>MLA 9:</strong> Author + page focus; widely used in the humanities.</p>

                <p><strong>Chicago style:</strong> Notes-bibliography or author–date; often used in history and publishing.</p>

                <p className="mt-2">Practical tip: Use a reference manager (Zotero, Mendeley). Capture metadata as you research, then insert citations as you write. Always proof the final bibliography for capitalization, italics, and DOI accuracy.</p>
              </div>
            </section>

            {/* Turnitin */}
            <section id="turnitin" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Interpreting Your Turnitin Similarity Report (Step-By-Step)</h2>
              <div className="text-gray-200 space-y-3">
                <p>Filter noise: Exclude the reference list and very short matches if permitted.</p>
                <p>Validate quotes: Ensure quotation marks and page numbers exist where your style requires them.</p>
                <p>Fix paraphrases: Rework any passage that mirrors source structure; add attribution.</p>
                <p>Synthesize: Merge overlapping ideas from multiple sources to create your own sentence structure.</p>
                <p>Re-scan if needed: After heavy edits, run the check again to confirm improvements.</p>
              </div>
            </section>

            {/* Workflow */}
            <section id="workflow" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">A Repeatable Workflow That Prevents Plagiarism Before It Starts</h2>
              <div className="text-gray-200 space-y-3">
                <p><strong>Plan:</strong> Define your research question and the sub-questions you’ll answer.</p>
                <p><strong>Research:</strong> Collect credible sources and tag them by theme in your manager.</p>
                <p><strong>Notes:</strong> Separate direct quotes, paraphrases, and your reflections; record page numbers.</p>
                <p><strong>Outline:</strong> Assign evidence to each section and note tentative citations.</p>
                <p><strong>Draft:</strong> Write from your outline, not from the source; apply the Read–Hide–Write–Check method.</p>
                <p><strong>Cite as you go:</strong> Insert in-text citations immediately.</p>
                <p><strong>Format references:</strong> Export and polish your reference list consistently (APA/Harvard/MLA/Chicago).</p>
                <p><strong>Review:</strong> Run a similarity scan; fix patchwriting and excess quotes.</p>
                <p><strong>Polish:</strong> Smooth transitions, verify style rules, and proofread for clarity.</p>
              </div>
            </section>

            {/* Consequences */}
            <section id="consequences" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Consequences and Integrity (Short, Sober, Helpful)</h2>
              <p className="text-gray-200">
                Academic communities protect trust. Breaches, intentional or not, can lead to resubmission, grade penalties, or formal misconduct procedures. The safest path is transparent sourcing, disciplined paraphrasing, and adherence to your institution’s guidance on AI and collaboration. This section reassures readers you take integrity seriously while giving them clear behavior to follow.
              </p>
            </section>

            {/* Support */}
            <section id="support" style={{ scrollMarginTop: "7rem" }} className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-2">What we provide</h2>
              <div className="text-gray-200 space-y-3">
                <p>If you’d like hands-on, ethical support while you implement this workflow, Infinite Solution can help you plan, outline, and refine your draft without compromising academic integrity.</p>
                <p>Our subject-matter experts guide you through thesis framing, paragraph-level outlining (with strong topic sentences and transitions), evidence selection, and critical analysis, then polish clarity with human proofreading and style edits (think active voice, cohesion, readability).</p>
                <p>We also handle compliance, APA 7th edition, Harvard referencing, MLA, adding correct in-text citations and a clean reference list. For peace of mind, we run an originality check with a trusted plagiarism checker (e.g., Turnitin) and can draft a brief AI disclosure when required.</p>
                <p>Tools like Zotero, Mendeley, Grammarly, and Hemingway Editor are integrated into our process, and our team offers responsive guidance for the UK, Europe, Australia, and the USA. You stay the author; we make the writing sharper, faster, and fully compliant.</p>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">FAQs: Fast Answers That Win Featured Snippets</h2>
              <div className="text-gray-200 space-y-3">
                <p><strong>Q: What is an acceptable similarity score in Turnitin?</strong><br />
                There’s no universal number. Educators look at why text matches citations, quotations, or references more than the percentage itself. Reduce overlaps by paraphrasing ethically and citing ideas.</p>

                <p><strong>Q: Can I paraphrase without citing?</strong><br />
                No. If the idea came from a source, cite it even when the wording is yours. Only common knowledge is exempt.</p>

                <p><strong>Q: Should I quote or paraphrase?</strong><br />
                Quote when exact wording is essential (legal definitions, distinctive phrasing). Otherwise paraphrase to demonstrate understanding and integrate the idea into your argument.</p>

                <p><strong>Q: Are AI tools allowed?</strong><br />
                Policies differ. If allowed, disclose use as required, verify facts, and never fabricate citations. If restricted, avoid AI-generated text and keep a clear research record.</p>

                <p><strong>Q: How do I credit images, figures, or tables?</strong><br />
                If you adapt or reuse them, acknowledge the source in the caption and reference list; comply with the license. When unsure, redraw and cite the data source.</p>
              </div>
            </section>

            {/* Conclusion */}
            <section id="conclusion" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Conclusion</h2>
              <p className="text-gray-200">
                To truly make your work plagiarism-free, focus less on the number and more on the practices: plan your argument, research with intention, paraphrase ethically, and cite consistently. Treat the Similarity Report as a diagnostic tool, not a verdict.
              </p>
            </section>
<section
  id="acknowledgement"
  style={{ scrollMarginTop: "7rem" }}
  className="bg-gradient-to-r from-indigo-900/40 via-purple-900/30 to-pink-900/20 rounded-xl border border-white/10 p-6 mt-10 shadow-lg"
>
  <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-300 pb-2">
    Acknowledgement
  </h2>

  <p className="text-gray-200">
    The concept was first introduced by{" "}
    <span className="font-semibold text-white">Abhishek Shaw</span>, who set the foundation for the content’s direction.
    <br />
    <a
      href="https://www.instagram.com/abhishekshawofficial_?igsh=Ynh2OXl6bm4xN3Z4"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 mt-2 text-pink-400 hover:text-pink-300 font-semibold"
    >
      <FaInstagram /> @abhishekshaw
    </a>
  </p>

  <p className="text-gray-200 mt-4">
    To shape the initial draft, we utilized advanced AI tools like <span className="font-semibold">ChatGPT</span> and{" "}
    <span className="font-semibold">Google Gemini</span>, ensuring speed, structure, and creativity. Once the draft was
    developed, our team at{" "}
    <a
      href="https://www.infinitesolutionedu.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-400 font-semibold hover:text-blue-300 underline"
    >
      Infinite Solution
    </a>{" "}
    took over to polish it, optimizing for SEO, refining the tone for clarity, and aligning the message with the target
    audience.
  </p>

  <p className="text-gray-200 mt-4">
    This collaborative approach seamlessly blended human expertise with AI-driven efficiency, resulting in content that
    is impactful, high-performing, and highly relevant to readers.
  </p>
</section>

          </article>
        </div>
      </div>
    </section>
  );
}
